'use client'

import { createContext, useContext, useCallback, useEffect, useMemo, useState, ReactNode } from 'react'
import {
  getToken,
  getUser,
  setToken as setAuthToken,
  setUser,
  removeToken,
  setAdminEmailCookie,
  type User,
} from '@/lib/auth'
import { getMe } from '@/lib/api'

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  login: (token: string, user: User) => void
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null)
  const [token, setTokenState] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUserData = useCallback(async (authToken: string) => {
    try {
      const userData = await getMe(authToken)
      if (userData) {
        const user = userData.data || userData
        setUserState(user)
        setUser(user)
        if (user.email) {
          setAdminEmailCookie(user.email)
        }
      }
      // If getMe returns null, keep using stored user data
    } catch (error) {
      console.error('Error refreshing user:', error)
      // Don't logout on error, just log it
      // The user might still be valid, just the endpoint doesn't work
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // Load user from localStorage on mount
    const storedToken = getToken()
    const storedUser = getUser()

    if (storedToken && storedUser) {
      setTokenState(storedToken)
      setUserState(storedUser)
      // Set cookie for middleware if token exists in localStorage but not in cookie
      // This handles the case where user was already logged in before cookie was implemented
      if (typeof window !== 'undefined') {
        const existingCookie = document.cookie.split(';').find(c => c.trim().startsWith('auth-token='))
        if (!existingCookie) {
          setAuthToken(storedToken) // This will set both localStorage and cookie
        }
        // Ensure admin-email cookie is present for already-logged-in sessions
        const adminEmailCookie = document.cookie.split(';').find(c => c.trim().startsWith('admin-email='))
        if (!adminEmailCookie && storedUser.email) {
          setAdminEmailCookie(storedUser.email)
        }
      }
      // Optionally verify token is still valid
      void refreshUserData(storedToken)
    } else {
      setIsLoading(false)
    }
  }, [refreshUserData])

  const login = useCallback((authToken: string, userData: User) => {
    setAuthToken(authToken)
    setUser(userData)
    setTokenState(authToken)
    setUserState(userData)
    if (userData.email) {
      setAdminEmailCookie(userData.email)
    }
  }, [])

  const logout = useCallback(() => {
    removeToken()
    setTokenState(null)
    setUserState(null)
    window.location.href = '/prijava'
  }, [])

  const refreshUser = useCallback(async () => {
    const currentToken = getToken()
    if (currentToken) {
      await refreshUserData(currentToken)
    }
  }, [refreshUserData])

  const value = useMemo(
    () => ({
      user,
      token,
      isLoading,
      login,
      logout,
      refreshUser,
    }),
    [user, token, isLoading, login, logout, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export { AuthProvider }

